// BandHowTo.jsx  →  same folder as MagicBand.jsx
// "MagicBeacon Tour" for Band+ on the DISPLAY (1280x800, touch).
// Opens on the tile screen. Each tile = one feature guide with numbered slides.
// Previous / Next / Close. Flat styling, Lato, no blur.
//
// Screenshots: public/static/bandhowto/  (1280x800 JPGs)
// To swap a placeholder, just overwrite the JPG with the same file name.
// `ring` = green highlight box over the screenshot, in % of the image
//          { left, top, width, height }. `arrow: true` adds a bouncing arrow under it.
//          Only add a ring once the real screenshot is in.

import React, { useState, useEffect } from "react";
const IMG = "/static/bandhowto/";
const SARAH_SRC = "/static/castmember-one.png";
const TOUR_VOICE_SRC = "/static/voice-tour.mp3"; // Sarah's line — silent until the file exists
const IMPORTANT_VOICE_SRC = "/static/voice-important.mp3"; // Sarah's IMPORTANT-page line — silent until the file exists

export const GUIDES = [
  {
    key: "sync",
    label: "Sync Bands to LIVE Shows",
    icon: "fireworks",
    slides: [
            {
        title: "Open Live Show Sync",
        body: <>From the Band+ menu, tap <b>VIP Live Show Sync</b>.</>,
        img: IMG + "sync-1.jpg",
      },
      {
        title: "Pick Where to Watch From",
        body: (
          <>
            <b>Magical MultiView:</b> watch a LIVE streamer inside the park.
            <br /><br />
            <b>LIVE Close-Up Fireworks:</b> our dedicated fireworks camera. Your bands sync automatically when the show begins.
          </>
        ),
        img: IMG + "sync-2.jpg",
      },
      {
        title: "Sync Your Band!",
        body: <>5 minutes before showtime, tap the purple <b>Sync Your Band!</b> button in the top right when prompted. If more than one streamer is covering the show, you can pick your favorite.</>,
        video: IMG + "sync-3.mp4",
      },
      {
        title: "Running Late?",
        body: <>If the show already started, the button may say <b>Syncing to show</b> with a countdown. Once it turns <b>green</b> and says <b>Connected! TAP HERE</b>, tap to jump in mid-show.</>,
        video: IMG + "sync-4.mp4",
      },
      {
        title: "Syncing to Bands",
        body: <>Hang tight while your bands sync. When the circle turns <b>green</b>, you're synced and the show opens.</>,
        video: IMG + "sync-5.mp4",
      },
      {
        title: "Enjoy the Show!",
        body: <>The system will identify the show in 2 minutes or less. Once the screen says <b>Connected! Enjoy the show!</b> you are fully connected and your bands will start to flash, vibrate and react in real-time to the show.</>,
        video: IMG + "sync-6.mp4",
      },
    ],
  },
  {
    key: "tink",
    label: "Tink Alert",
    icon: "wand",
    slides: [
            {
        title: "Turn On Tink Alert",
        body: <>In the top bar, tap <b>Tink Alert</b> to turn it on. Your bands will vibrate & flash <b>yellow and green</b> each night when Tink flies over the castle!</>,
        video: IMG + "tink-1.mp4",
      },
    ],
  },
  {
    key: "color",
    label: "Color Select",
    icon: "palette",
    slides: [
      {
        title: "How Many Colors?",
        body: <>Tap <b>ONE</b> for a single color, or <b>TWO</b> or <b>THREE+</b> to design your band with multiple colors.</>,
        img: IMG + "color-1.jpg",
      },
      {
        title: "Pick Your Colors",
        body: <>For ONE, just tap a color. For TWO or THREE+, <b>tap a zone on the band</b>, then choose a color. The band on screen shows your design.</>,
        img: IMG + "color-2.jpg",
      },
      {
        title: "Send the Magic",
        body: <>Tap <b>Next</b>, then <b>Send Now!</b></>,
        img: IMG + "color-3.jpg",
      },
      {
        title: "Watch Your Bands Light Up",
        body: <>It may take up to <b>20 seconds</b> for all bands to respond. Already lit? Tap the <b>"Already illuminated?"</b> button to pick another color.</>,
        img: IMG + "color-4.jpg",
      },
      
    ],
  },
  {
    key: "timers",
    label: "Timers & Alerts",
    icon: "timer",
    slides: [
            {
        title: "Open Interact",
        body: <>On the main menu, tap <b>INTERACT</b>, then select <b>Band Scanner</b>, <b>Timer</b>, or <b>Alerts</b>.</>,
        img: IMG + "timers-1.jpg",
      },
      {
        title: "Band Scanner",
        body: <>Enjoy your virtual admission into the parks! Scan the MagicBand scanner and watch your bands interact with the screen, just like you're stepping into your happy place. <b>ProTip:</b> Turn on <b>DVC</b> to be welcomed home!</>,
        img: IMG + "timers-2.jpg",
      },
      {
        title: "Set a Timer",
        body: (
          <>
            Set your timer and press <b>Start</b>. Your bands will <b>flash &amp; vibrate</b> when time is up. Tap <b>Stop Alarm</b> to end it.
            <br /><br />
            <span style={{ display: "block", fontSize: 16, lineHeight: 1.35, fontStyle: "italic" }}>IMPORTANT! For fun only. Don't rely on the timer or bands for anything time-critical.</span>
          </>
        ),
        img: IMG + "timers-3.jpg",
      },
      {
        title: "Send an Alert",
        body: <>Each alert has its own message, like <b>Dinner Time</b> or <b>Time to Leave</b>. Tap an alert and everyone's bands light up in that color.</>,
        img: IMG + "timers-4.jpg",
      },
      {
        title: "Make It Yours",
        body: <>Change what each color means. Pick a label or create your own.</>,
        img: IMG + "timers-5.jpg",
      },
    ],
  },
];

export const Icon = ({ name }) => {
  const p = { fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" };
  if (name === "wand")
    return (
      <svg viewBox="0 0 24 24" {...p} strokeWidth={1.9}>
        <path d="M3 21L13.5 10.5" />
        <path d="M16 3l1.2 3.1L20.3 7.3 17.2 8.5 16 11.6 14.8 8.5 11.7 7.3 14.8 6.1z" fill="currentColor" />
        <path d="M20.5 13.5l.5 1.2 1.2.5-1.2.5-.5 1.2-.5-1.2-1.2-.5 1.2-.5z" fill="currentColor" strokeWidth={1} />
        <path d="M9.5 3.5l.4 1 1 .4-1 .4-.4 1-.4-1-1-.4 1-.4z" fill="currentColor" strokeWidth={1} />
      </svg>
    );
  if (name === "palette")
    return (
      <svg viewBox="0 0 24 24" {...p}>
        <path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.5-.8 1.5-1.5 0-.9-.6-1.2-.6-2 0-.8.7-1.5 1.6-1.5H17a4 4 0 0 0 4-4c0-5-4-9-9-9z" />
        <circle cx="7.5" cy="11" r="1.2" />
        <circle cx="10.5" cy="7.5" r="1.2" />
        <circle cx="15" cy="7.5" r="1.2" />
      </svg>
    );
  if (name === "timer")
    return (
      <svg viewBox="0 0 24 24" {...p}>
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2.5 2.5" />
        <path d="M9 2h6" />
      </svg>
    );
  // fireworks: three bursts with sparks at the tips
  return <svg viewBox="0 0 24 24" {...p} strokeWidth={1.1}><path d="M9.00 8.40L9.00 4.20"/><circle cx="9.00" cy="2.90" r="0.55" fill="currentColor" stroke="none"/><path d="M9.99 8.60L11.03 6.10"/><circle cx="11.53" cy="4.90" r="0.55" fill="currentColor" stroke="none"/><path d="M10.84 9.16L13.81 6.19"/><circle cx="14.73" cy="5.27" r="0.55" fill="currentColor" stroke="none"/><path d="M11.40 10.01L13.90 8.97"/><circle cx="15.10" cy="8.47" r="0.55" fill="currentColor" stroke="none"/><path d="M11.60 11.00L15.80 11.00"/><circle cx="17.10" cy="11.00" r="0.55" fill="currentColor" stroke="none"/><path d="M11.40 11.99L13.90 13.03"/><circle cx="15.10" cy="13.53" r="0.55" fill="currentColor" stroke="none"/><path d="M10.84 12.84L13.81 15.81"/><circle cx="14.73" cy="16.73" r="0.55" fill="currentColor" stroke="none"/><path d="M9.99 13.40L11.03 15.90"/><circle cx="11.53" cy="17.10" r="0.55" fill="currentColor" stroke="none"/><path d="M9.00 13.60L9.00 17.80"/><circle cx="9.00" cy="19.10" r="0.55" fill="currentColor" stroke="none"/><path d="M8.01 13.40L6.97 15.90"/><circle cx="6.47" cy="17.10" r="0.55" fill="currentColor" stroke="none"/><path d="M7.16 12.84L4.19 15.81"/><circle cx="3.27" cy="16.73" r="0.55" fill="currentColor" stroke="none"/><path d="M6.60 11.99L4.10 13.03"/><circle cx="2.90" cy="13.53" r="0.55" fill="currentColor" stroke="none"/><path d="M6.40 11.00L2.20 11.00"/><circle cx="0.90" cy="11.00" r="0.55" fill="currentColor" stroke="none"/><path d="M6.60 10.01L4.10 8.97"/><circle cx="2.90" cy="8.47" r="0.55" fill="currentColor" stroke="none"/><path d="M7.16 9.16L4.19 6.19"/><circle cx="3.27" cy="5.27" r="0.55" fill="currentColor" stroke="none"/><path d="M8.01 8.60L6.97 6.10"/><circle cx="6.47" cy="4.90" r="0.55" fill="currentColor" stroke="none"/><path d="M19.22 16.42L19.60 14.56"/><circle cx="19.85" cy="13.29" r="0.4" fill="currentColor" stroke="none"/><path d="M19.81 16.76L20.72 15.92"/><circle cx="21.68" cy="15.04" r="0.4" fill="currentColor" stroke="none"/><path d="M20.09 17.37L21.98 17.16"/><circle cx="23.27" cy="17.01" r="0.4" fill="currentColor" stroke="none"/><path d="M19.96 18.04L21.04 18.65"/><circle cx="22.17" cy="19.29" r="0.4" fill="currentColor" stroke="none"/><path d="M19.46 18.50L20.25 20.23"/><circle cx="20.79" cy="21.41" r="0.4" fill="currentColor" stroke="none"/><path d="M18.78 18.58L18.54 19.79"/><circle cx="18.28" cy="21.07" r="0.4" fill="currentColor" stroke="none"/><path d="M18.19 18.24L16.79 19.53"/><circle cx="15.83" cy="20.41" r="0.4" fill="currentColor" stroke="none"/><path d="M17.91 17.63L16.68 17.77"/><circle cx="15.38" cy="17.91" r="0.4" fill="currentColor" stroke="none"/><path d="M18.04 16.96L16.39 16.02"/><circle cx="15.26" cy="15.39" r="0.4" fill="currentColor" stroke="none"/><path d="M18.54 16.50L18.03 15.37"/><circle cx="17.49" cy="14.19" r="0.4" fill="currentColor" stroke="none"/><path d="M19.77 3.64L20.21 2.21"/><circle cx="20.59" cy="0.97" r="0.35" fill="currentColor" stroke="none"/><path d="M20.30 4.08L21.62 3.38"/><circle cx="22.77" cy="2.77" r="0.35" fill="currentColor" stroke="none"/><path d="M20.36 4.77L21.79 5.21"/><circle cx="23.03" cy="5.59" r="0.35" fill="currentColor" stroke="none"/><path d="M19.92 5.30L20.62 6.62"/><circle cx="21.23" cy="7.77" r="0.35" fill="currentColor" stroke="none"/><path d="M19.23 5.36L18.79 6.79"/><circle cx="18.41" cy="8.03" r="0.35" fill="currentColor" stroke="none"/><path d="M18.70 4.92L17.38 5.62"/><circle cx="16.23" cy="6.23" r="0.35" fill="currentColor" stroke="none"/><path d="M18.64 4.23L17.21 3.79"/><circle cx="15.97" cy="3.41" r="0.35" fill="currentColor" stroke="none"/><path d="M19.08 3.70L18.38 2.38"/><circle cx="17.77" cy="1.23" r="0.35" fill="currentColor" stroke="none"/></svg>;
};

const CSS = `
.bht-back{position:fixed;inset:0;z-index:10050;background:rgba(10,14,28,.84);display:flex;align-items:center;justify-content:center;font-family:'Lato',sans-serif;}
.bht-card{position:relative;width:1180px;max-width:96vw;background:#fff;border-radius:24px;overflow:hidden;color:#1a2542;box-shadow:0 12px 40px rgba(10,14,28,.35);}
.bht-bar{display:flex;align-items:center;gap:16px;background:#007bff;padding:16px 22px 16px 32px;min-height:80px;box-sizing:border-box;}
.bht-rainbow{height:6px;background:linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#B6E04A,#3EE08A,#3CC9FF,#6FB0FF,#8F9BFF,#B48CFF,#E07CFF,#FF6FC8);}
.bht-backbtn{flex:0 0 auto;width:46px;height:46px;border-radius:999px;border:2px solid rgba(255,255,255,.7);background:transparent;color:#fff;font-size:28px;font-weight:900;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0 0 3px;}
.bht-bartitle{flex:1;font-size:30px;font-weight:900;color:#fff;letter-spacing:-.2px;}
.bht-x{flex:0 0 auto;width:48px;height:48px;border-radius:999px;border:2px solid rgba(255,255,255,.7);background:transparent;color:#fff;font-size:22px;font-weight:900;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;}
.bht-backbtn:active,.bht-x:active{transform:scale(.94);}
.bht-body-wrap{padding:26px 32px 24px;}
.bht-intro{font-size:28px;font-weight:700;color:#1a2542;margin:0 0 22px;}
.bht-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;}
.bht-tile{height:250px;border-radius:18px;border:3px solid #d8dce3;background:#fff;color:#1a2542;font-family:'Lato',sans-serif;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;padding:16px;text-align:center;}
.bht-tile:active{transform:scale(.97);border-color:#007bff;}
.bht-ico{width:84px;height:84px;border-radius:22px;background:#007bff;color:#fff;display:flex;align-items:center;justify-content:center;}
.bht-ico svg{width:48px;height:48px;}
.bht-tile-label{font-size:22px;font-weight:900;line-height:1.2;}
.bht-row{display:flex;gap:32px;align-items:stretch;}
.bht-shot{position:relative;flex:0 0 640px;height:400px;border-radius:14px;overflow:hidden;border:1px solid #d8dce3;background:#f1f3f7;}
.bht-shot img,.bht-shot video{display:block;width:100%;height:100%;object-fit:cover;}
.bht-ring{position:absolute;border:4px solid #05f651;border-radius:12px;animation:bhtPulse 1.4s ease-in-out infinite;pointer-events:none;}
@keyframes bhtPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
.bht-arrow{position:absolute;width:52px;height:52px;margin-left:-26px;pointer-events:none;animation:bhtBob 1.1s ease-in-out infinite;}
@keyframes bhtBob{0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}}
.bht-noshot{flex:0 0 640px;height:400px;border-radius:14px;border:2px dashed #d8dce3;background:#f7f8fa;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#8a93a6;}
.bht-text{flex:1;display:flex;flex-direction:column;justify-content:center;min-width:0;}
.bht-count{font-size:20px;font-weight:900;color:#007bff;margin:0 0 8px;}
.bht-title{font-size:34px;font-weight:900;line-height:1.1;margin:0 0 14px;color:#1a2542;letter-spacing:-.4px;}
.bht-body{font-size:22px;font-weight:400;line-height:1.45;color:#46506b;margin:0;}
.bht-body b{color:#1a2542;font-weight:900;}
.bht-nav{display:flex;align-items:center;justify-content:space-between;margin-top:22px;}
.bht-btn{min-width:200px;height:62px;border-radius:14px;border:3px solid #d8dce3;background:#fff;color:#1a2542;font-family:'Lato',sans-serif;font-size:21px;font-weight:900;cursor:pointer;}
.bht-btn:active{transform:scale(.97);}
.bht-next{border-color:#007bff;background:#007bff;color:#fff;}
.bht-dots{display:flex;gap:8px;}
.bht-pip{width:10px;height:10px;border-radius:999px;background:#d8dce3;}
.bht-pip.on{width:28px;background:#007bff;}
.bht-guide{display:flex;gap:12px;align-items:flex-end;margin:-4px 0 -24px -20px;}
.bht-sarah{flex:0 0 230px;height:340px;overflow:hidden;}
.bht-sarah img{display:block;width:100%;height:auto;}
.bht-guide-right{flex:1;min-width:0;padding-bottom:24px;}
.bht-bubble{position:relative;background:#fff;border:3px solid #1a2542;border-radius:18px;padding:14px 20px;font-size:22px;font-weight:700;line-height:1.35;color:#1a2542;margin:0 0 20px 6px;}
.bht-bubble:before{content:"";position:absolute;left:-16px;top:22px;border:13px solid transparent;border-right-color:#1a2542;border-left:0;}
.bht-bubble:after{content:"";position:absolute;left:-11px;top:24px;border:11px solid transparent;border-right-color:#fff;border-left:0;}
.bht-guide .bht-tiles{gap:14px;}
.bht-guide .bht-tile{height:220px;gap:10px;padding:14px;background:#f7f9fc;}
.bht-guide .bht-ico{width:72px;height:72px;border-radius:20px;}
.bht-guide .bht-ico svg{width:42px;height:42px;}
.bht-guide .bht-tile-label{font-size:20px;}
.bht-learn{font-size:15px;font-weight:900;color:#007bff;}
.bht-foot{border-top:1px solid #e6e9ef;padding:14px 32px;text-align:center;font-size:17px;color:#6b7590;background:#fafbfd;}
.bht-foot b{color:#007bff;}
.bht-note{background:#fff;border:3px solid #007bff;border-radius:18px;padding:18px 22px;}
.bht-note-head{display:inline-block;background:#e5484d;color:#fff;font-size:15px;font-weight:900;letter-spacing:2px;border-radius:8px;padding:5px 12px;margin-bottom:12px;}
.bht-note-row{display:flex;align-items:center;gap:14px;font-size:21px;line-height:1.35;color:#1a2542;margin:0 0 10px;}
.bht-note-row b{font-weight:900;}
.bht-note-num{flex:0 0 auto;width:34px;height:34px;border-radius:50%;background:#1a2542;color:#fff;font-size:18px;font-weight:900;display:flex;align-items:center;justify-content:center;}
.bht-note-btn{display:block;margin:14px 0 0 auto;}
`;

export default function BandHowTo({ onClose }) {
  const [g, setG] = useState(null); // null = tile screen
  const [i, setI] = useState(0);
  const [broken, setBroken] = useState({}); // img src -> true if it failed to load

  const guide = GUIDES.find((x) => x.key === g);
  
    // Opens on the IMPORTANT page; "Got It" moves to the feature tiles.
  const [intro, setIntro] = useState(true);

  // Sarah's line for each screen (silent if the mp3 isn't there yet).
  useEffect(() => {
    let a;
    try { a = new Audio(intro ? IMPORTANT_VOICE_SRC : TOUR_VOICE_SRC); a.volume = 0.9; const p = a.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {}
    return () => { try { if (a) a.pause(); } catch (e) {} };
  }, [intro]);

  // ---------- Tile screen ----------
  if (!guide) {
    return (
      <div className="bht-back">
        <style>{CSS}</style>
        <div className="bht-card">
          <div className="bht-bar">
            <div className="bht-bartitle">Welcome to the MagicBeacon Tour!</div>
            <button className="bht-x" onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="bht-rainbow" />
          <div className="bht-body-wrap">
                        <div className="bht-guide">
              <div className="bht-sarah"><img key="sarah" src={SARAH_SRC} alt="" draggable="false" /></div>
              <div className="bht-guide-right">
                <div className="bht-bubble">{intro ? "Hi, I'm Sarah! Before we start, two important things about your MagicBands..." : "Now pick a feature and I'll walk you through how to use it!"}</div>
                {intro && (
                  <div className="bht-note">
                    <div className="bht-note-head">IMPORTANT</div>
                    <div className="bht-note-row"><span className="bht-note-num">1</span><span>MagicBands must be <b>fully charged</b> for optimal performance.</span></div>
                    <div className="bht-note-row"><span className="bht-note-num">2</span><span>MagicBands <b>will not work while charging</b> or while clipped into the charger.</span></div>
                    <button className="bht-btn bht-next bht-note-btn" onClick={() => setIntro(false)}>Got It! Show Me Around ›</button>
                  </div>
                )}
                <div className="bht-tiles" style={intro ? { display: "none" } : undefined}>
                  {GUIDES.map((t) => (
                    <button key={t.key} className="bht-tile" onClick={() => { setG(t.key); setI(0); }}>
                      <span className="bht-ico"><Icon name={t.icon} /></span>
                      <span className="bht-tile-label">{t.label}</span>
                      
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bht-foot">You can reopen this guide anytime from the <b>"How To"</b> button on the menu bar.</div>
        </div>
      </div>
    );
  }

  // ---------- Guide slides ----------
  const s = guide.slides[i];
  const last = i === guide.slides.length - 1;
  const showImg = s.img && !broken[s.img];

  return (
    <div className="bht-back">
      <style>{CSS}</style>
      <div className="bht-card">
        <div className="bht-bar">
          <button className="bht-backbtn" onClick={() => setG(null)} aria-label="Back to the tour">‹</button>
          <div className="bht-bartitle">{guide.label}</div>
          <button className="bht-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="bht-rainbow" />
        <div className="bht-body-wrap">
          <div className="bht-row">
                        {s.video ? (
              <div className="bht-shot">
                <video key={s.video} src={s.video} ref={(v) => { if (v) { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); } }} muted autoPlay loop playsInline preload="auto" disablePictureInPicture controls={false} />
              </div>
            ) : showImg ? (
              <div className="bht-shot">
                <img
                  key={s.img}
                  src={s.img}
                  alt=""
                  draggable="false"
                  onError={() => setBroken((b) => ({ ...b, [s.img]: true }))}
                />
                {s.ring && (
                  <div
                    className="bht-ring"
                    style={{ left: s.ring.left + "%", top: s.ring.top + "%", width: s.ring.width + "%", height: s.ring.height + "%" }}
                  />
                )}
                {s.ring && s.arrow && (
                  <svg
                    className="bht-arrow"
                    viewBox="0 0 56 56"
                    style={{ left: s.ring.left + s.ring.width / 2 + "%", top: "calc(" + (s.ring.top + s.ring.height) + "% + 10px)" }}
                    aria-hidden="true"
                  >
                    <path d="M28 6 L48 30 H36 V50 H20 V30 H8 Z" fill="#05f651" stroke="#1a2542" strokeWidth="3" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            ) : (
              <div className="bht-noshot">Screenshot coming soon</div>
            )}

            <div className="bht-text">
              <div className="bht-count">{i + 1}/{guide.slides.length}</div>
              <h2 className="bht-title">{s.title}</h2>
              <p className="bht-body">{s.body}</p>
            </div>
          </div>

          <div className="bht-nav">
            <button className="bht-btn" onClick={() => (i === 0 ? setG(null) : setI(i - 1))}>‹ Previous</button>
            <div className="bht-dots">
              {guide.slides.map((_, k) => (
                <span key={k} className={"bht-pip" + (k === i ? " on" : "")} />
              ))}
            </div>
            <button className="bht-btn bht-next" onClick={() => (last ? setG(null) : setI(i + 1))}>
              {last ? "Got It!" : "Next ›"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
